import { get } from 'lodash';
import classNames from 'classnames';
import { createToolbarTheme } from './utils';
import {
  POST_TEXT_COLOR,
  LINK_AND_HASHTAG_COLOR,
  POST_HOMEPAGE_LINK_AND_HASHTAG_COLOR,
  CARD_BACKGROUND_COLOR,
  BUTTON_COLOR,
  POST_PAGE_FONT_PATH,
  POST_PAGE_FONT_SIZE_MOBILE_PATH,
  IS_POST_ADVANCED_DESIGN_OPTIONS_ENABLED_PATH,
  POST_HEADER_TWO_FONT_SIZE_MOBILE_PATH,
  POST_HEADER_THREE_FONT_SIZE_MOBILE_PATH,
  POST_QUOTES_FONT_SIZE_MOBILE_PATH,
} from '@wix/communities-blog-client-common/dist/src/constants/wix-params';
import {
  DEFAULT_POST_PAGE_FONT_SIZE_MOBILE,
  DEFAULT_POST_HEADER_TWO_FONT_SIZE_MOBILE,
  DEFAULT_POST_HEADER_THREE_SIZE_MOBILE,
  DEFAULT_POST_QUOTES_SIZE_MOBILE,
} from '../../app/constants/post-page';
import { getSiteColor, setColorOpacity } from '../../app/services/colors';
import applyFixedBusinessManagerStylesToTheme from './business-manager-theme';
import styles from './theme.scss';

export default function getTheme({
  appSettings,
  readOnly,
  isCompact,
  tooltipsEnabled,
  isViewer,
  isSeo,
  isMobile,
  isPostPageEnabled,
  richContentFontClassName,
  useDescriptionFontInEditor,
  isPostPage,
  css,
}) {
  const textColor = getSiteColor(appSettings, {
    wixParam: POST_TEXT_COLOR,
    presetFallback: 'color-5',
    colorFallback: '#000',
  });
  const opaqueBackgroundColor = getSiteColor(appSettings, {
    wixParam: CARD_BACKGROUND_COLOR,
    presetFallback: 'color-1',
    colorFallback: '#fff',
    opacity: 1,
  });
  const dividerColor = setColorOpacity(textColor, 0.2);
  const activeDividerColor = setColorOpacity(textColor, 0.5);
  const highlightedColor = getSiteColor(appSettings, {
    wixParam: BUTTON_COLOR,
    presetFallback: 'color-8',
    colorFallback: '#3899ec',
    opacity: 1,
  });

  const linkHashtagColor =
    isPostPage === false
      ? getSiteColor(appSettings, {
          wixParam: POST_HOMEPAGE_LINK_AND_HASHTAG_COLOR,
          presetFallback: 'color-8',
          colorFallback: '#44C2BC',
        })
      : getSiteColor(appSettings, {
          wixParam: LINK_AND_HASHTAG_COLOR,
          presetFallback: 'color-8',
          colorFallback: '#44C2BC',
        });

  const mobileFontSize = isPostPageEnabled
    ? get(appSettings, POST_PAGE_FONT_SIZE_MOBILE_PATH, DEFAULT_POST_PAGE_FONT_SIZE_MOBILE)
    : DEFAULT_POST_PAGE_FONT_SIZE_MOBILE;

  const classes = {
    textColor: css`
      color: ${textColor};
    `,
    textBorderColor: css`
      border-color: ${textColor};
    `,
    textBorderRightColor: css`
      border-right-color: ${textColor};
    `,
    textBorderBottomColor: css`
      border-bottom-color: ${textColor};
    `,
    opaqueBackgroundColor: css`
      background-color: ${opaqueBackgroundColor};
    `,
    dividerBorderColor: css`
      border-color: ${dividerColor};
    `,
    dividerBackgroundColor: css`
      background-color: ${dividerColor};
    `,
    focusedInputBorderColor: css`
      &:focus {
        border-color: ${activeDividerColor};
      }
    `,
    highlightedBorderColor: css`
      border-color: ${highlightedColor};
    `,
    highlightedBackgroundColor: css`
      background-color: ${highlightedColor};
    `,
    highlightedColor: css`
      color: ${highlightedColor};
    `,
    mobileFontSize: css`
      font-size: ${mobileFontSize}px;
    `,
  };

  const { size: textFontSize } = get(appSettings, POST_PAGE_FONT_PATH, { size: 18 });
  classes.headerTwoFontSize = css`
    font-size: ${Math.min(textFontSize + 10, 38)}px !important;
  `;
  classes.headerThreeFontSize = css`
    font-size: ${Math.min(textFontSize + 4, 32)}px !important;
  `;

  const imageContainerClass = 'image-container';
  const imageCaptionClass = 'image-caption';
  const toolbarButtonWrapperClass = 'rce-toolbar-button-wrapper';
  const toolbarButtonActiveClass = 'rce-toolbar-button-active';

  const defaultToolbarTheme = {
    toolbar: classNames(classes.opaqueBackgroundColor, classes.dividerBorderColor, styles.toolbarShadow),
    toolbarButton_wrapper: classNames(toolbarButtonWrapperClass, styles.toolbarButtonWrapper),
    toolbarButton_active: toolbarButtonActiveClass,
    toolbarButton_icon: classNames(
      classes.textColor,
      css`
        .${toolbarButtonWrapperClass}:hover &,
        .${toolbarButtonWrapperClass}:active &,
        .${toolbarButtonActiveClass} & {
          color: ${highlightedColor};
        }
      `,
    ),
    toolbarSeparator: classes.dividerBackgroundColor,
    toolbar_scrollableContainer: styles.toolbarScrollableContainer,
  };

  const mobileHeaderTwoFontSize = get(
    appSettings,
    POST_HEADER_TWO_FONT_SIZE_MOBILE_PATH,
    DEFAULT_POST_HEADER_TWO_FONT_SIZE_MOBILE,
  );
  const mobileHeaderThreeFontSize = get(
    appSettings,
    POST_HEADER_THREE_FONT_SIZE_MOBILE_PATH,
    DEFAULT_POST_HEADER_THREE_SIZE_MOBILE,
  );
  const mobileQuotesFontSize = get(appSettings, POST_QUOTES_FONT_SIZE_MOBILE_PATH, DEFAULT_POST_QUOTES_SIZE_MOBILE);
  const advancedPostOpts = get(appSettings, IS_POST_ADVANCED_DESIGN_OPTIONS_ENABLED_PATH, false);
  const mobileHeaderTwoStyles = isMobile
    ? css(`
    & {
      font-size: ${mobileHeaderTwoFontSize}px !important;
    }
  `)
    : '';
  const mobileHeaderThreeStyles = isMobile
    ? css(`
  & {
    font-size: ${mobileHeaderThreeFontSize}px !important;
  }
`)
    : '';
  const mobileQuotesStyles = isMobile
    ? css(`
    & {
      font-size: ${mobileQuotesFontSize}px !important;
    }
  `)
    : '';

  const headerTwoStyles = advancedPostOpts
    ? classNames('blog-post-header-two-font', 'blog-post-header-two-color', mobileHeaderTwoStyles)
    : classNames('blog-post-title-font', styles.headerTwo, classes.headerTwoFontSize, mobileHeaderTwoStyles);

  const headerThreeStyles = advancedPostOpts
    ? classNames('blog-post-header-three-font', 'blog-post-header-three-color', mobileHeaderThreeStyles)
    : classNames('blog-post-title-font', styles.headerThree, classes.headerThreeFontSize, mobileHeaderThreeStyles);

  const quoteStyles = advancedPostOpts
    ? classNames(
        styles.quote,
        'blog-post-quote-font',
        'blog-post-quote-color',
        'blog-quote-border-color',
        mobileQuotesStyles,
      )
    : classNames(styles.quote, styles.advancedQuoteStylesOff, 'blog-quote-border-color', mobileQuotesStyles);

  const blogSharedValues = {
    colors: {
      linkHashtagColor,
    },
    classes: {
      imageContainerClass,
      imageCaptionClass,
      toolbarButtonWrapperClass,
      toolbarButtonActiveClass,
    },
  };

  return {
    advancedPostOpts,
    blogSharedValues,
    editor: classNames(
      styles.editor,
      isSeo && styles.seo,
      useDescriptionFontInEditor ? richContentFontClassName : styles.editorDesktopFontSize,
      isMobile && classes.mobileFontSize,
      css`
        a {
          .${imageContainerClass} {
            .${imageCaptionClass} {
              color: ${linkHashtagColor};

              &:hover {
                text-decoration: underline;
              }
            }
          }
        }
      `,
    ),
    wrapper: classNames(styles.wrapper, richContentFontClassName, {
      [styles.readOnly]: readOnly,
      [styles.sizeCompact]: isCompact,
    }),
    desktop: styles.desktop,
    android: styles.android,

    sizeFullWidth: styles.sizeFullWidth,
    sizeSmall: styles.sizeSmall,
    textWrapNowrap: styles.textWrapNowrap,

    text: classNames(styles.text, 'blog-post-text-font', 'blog-post-text-color'),
    quote: quoteStyles,
    headerOne: classNames('blog-post-title-font', styles.headerTwo, classes.headerTwoFontSize),
    headerTwo: headerTwoStyles,
    headerThree: headerThreeStyles,
    orderedList: classNames(styles.text, styles.listItem, styles.orderedListItem),
    orderedListContainer: styles.orderedListContainer,
    unorderedList: classNames(styles.text, styles.listItem, styles.unorderedListItem),
    unorderedListContainer: styles.unorderedListContainer,
    atomic: styles.atomic,

    link: classNames('blog-link-hashtag-color', styles.link),
    hashtag: classNames('blog-link-hashtag-color', styles.hashtag),

    pluginContainer: classNames(
      classes.highlightedBorderColor,
      styles.pluginContainer,
      css`
        & {
          & > div {
            .${imageContainerClass}:not(:only-child) {
              .${imageCaptionClass} {
                color: ${linkHashtagColor};
              }
            }
          }
        }
      `,
    ),
    panelContainer: classNames(classes.opaqueBackgroundColor, classes.dividerBorderColor, styles.toolbarShadow),
    hasFocus: classNames(classes.highlightedBorderColor, styles.hasFocus),
    focused: styles.focused,
    alignLeft: styles.alignLeft,
    alignRight: styles.alignRight,
    alignCenter: styles.alignCenter,

    htmlComponent: isViewer && styles.htmlComponent,
    pluginContainerMobile: isViewer && styles.pluginContainerMobile,

    left: isViewer && classNames(styles.left, styles.text),
    center: isViewer && classNames(styles.center, styles.text),
    right: isViewer && classNames(styles.right, styles.text),
    justify: isViewer && classNames(styles.justify, styles.text),

    ...createToolbarTheme('inline', {
      ...defaultToolbarTheme,
      toolbar_buttonWrapper: styles.inlineToolbarButtonWrapper,
    }),
    ...createToolbarTheme('side', {
      ...defaultToolbarTheme,
      toolbar_floatingIcon: [
        css`
          svg {
            fill: ${textColor};
          }
        `,
        css`
          &:hover svg {
            fill: ${highlightedColor};
          }
        `,
      ],
    }),
    ...createToolbarTheme('plugin', {
      ...defaultToolbarTheme,
      toolbar_inlinePanel: classes.dividerBorderColor,
    }),
    ...createToolbarTheme('mobile', {
      ...defaultToolbarTheme,
      toolbar_fixed: styles.mobileToolbarFixed,
      toolbar_responsiveArrow: classNames(classes.opaqueBackgroundColor, classes.dividerBorderColor),
      toolbar_responsiveArrowLeft_icon: classes.textBorderColor,
      toolbar_responsiveArrowRight_icon: classes.textBorderColor,
      toolbar_scrollableContainer: styles.mobileToolbar_scrollableContainer,
    }),
    ...createToolbarTheme('footer', {
      ...defaultToolbarTheme,
      toolbar: classNames(styles.footerToolbar, classes.dividerBorderColor),
    }),
    inlineToolbarDropdown_options: classNames(
      css`
        & {
          background-color: ${opaqueBackgroundColor};
        }
      `,
    ),
    inlineToolbarDropdownButton_icon: classNames(
      css`
        & {
          color: ${textColor};
        }
      `,
      css`
        &:hover {
          color: ${highlightedColor};
        }
      `,
    ),
    'Dropdown-root': classNames(richContentFontClassName, styles.dropdownRoot),
    'Dropdown-control': classNames(
      'blog-text-color',
      styles.dropdownControl,
      css`
        &:hover {
          color: ${highlightedColor};
        }
      `,
    ),
    'Dropdown-menu': classNames(classes.opaqueBackgroundColor, classes.dividerBorderColor),
    'Dropdown-arrow': styles.dropdownArrow,
    'Dropdown-option': classNames(
      'blog-text-color',
      css`
        &:hover {
          color: ${highlightedColor};
        }
      `,
    ),

    tooltip: classNames(
      css`
        &:hover::before,
        &:hover::after {
          background-color: ${textColor};
        }
      `,
      css`
        &:hover::before {
          color: ${opaqueBackgroundColor};
        }
      `,
      styles.tooltip,
      { [styles.tooltipHidden]: !tooltipsEnabled },
    ),

    tabs_headers: classes.dividerBorderColor,
    tabs_headers_option: css`
      & + & {
        border-left-color: ${dividerColor};
      }
    `,
    tabs_headers_option_selected: classes.highlightedBorderColor,
    tabs_headers_option_label: classNames(richContentFontClassName, styles.tabsHeadersOptionLabel),

    button_primary: classNames(
      richContentFontClassName,
      'blog-button-background-color',
      'blog-button-primary-text-color',
      styles.button,
    ),
    button_secondary: classNames(
      richContentFontClassName,
      classes.highlightedBorderColor,
      'blog-button-secondary-text-color',
      'blog-button-secondary-text-hover-color',
      styles.button,
      styles.buttonSecondary,
    ),

    checkbox_icon: classNames(styles.checkboxIcon, classes.highlightedBorderColor, styles.checkboxIcon),
    checkbox_icon_checked: classNames(
      styles.checkboxIconChecked,
      classes.highlightedBackgroundColor,
      'blog-button-primary-text-color',
    ),

    'divider-container': isViewer && styles.dividerContainer,
    gallery_container: isViewer && styles.gallery_container,

    inputWithLabel_input: classNames(
      'blog-text-color',
      classes.dividerBorderColor,
      classes.focusedInputBorderColor,
      styles.inputWithLabelInput,
    ),

    linkPanel_Content: classNames(richContentFontClassName, 'blog-text-color'),
    linkPanel_textInput: classNames(
      'blog-text-color',
      classes.dividerBorderColor,
      css`
        &:focus {
          border-color: ${activeDividerColor};
        }
      `,
      styles.linkPanelTextInput,
    ),

    radioGroup: classNames(richContentFontClassName, styles.radioGroup),
    radioGroup_input: styles.radioGroupInput,
    radioGroup_button: classNames('blog-button-color', styles.radioGroupButton),

    radioGroupHorizontal_title: classNames(richContentFontClassName, styles.radioGroupHorizontalTitle),

    slider: classNames('blog-button-color', styles.slider),

    sliderWithInput_label: classNames(richContentFontClassName, styles.sliderWithInputLabel),
    sliderWithInput_input: classNames(
      richContentFontClassName,
      'blog-text-color',
      styles.sliderWithInputInput,
      classes.dividerBorderColor,
    ),

    selectionListOption_selected: 'blog-text-color',

    layoutsSelector_label: classNames(richContentFontClassName, styles.layoutsSelectorLabel),
    layoutsSelector_tile_label: classNames(richContentFontClassName, styles.layoutsSelectorTitleLabel),
    layoutsSelector_icon_selected: classes.highlightedColor,

    thumbnailPlacementSelector_label: classNames(richContentFontClassName, styles.thumbnailPlacementSelectorLabel),
    thumbnailPlacementSelector_icon_selected: classes.highlightedColor,

    imageContainer: imageContainerClass,
    video_container: styles.video_container,

    imageRatioSelector_label: classNames(richContentFontClassName, styles.imageRatioSelectorLabel),
    imageRatioSelector_ratioLabel: classNames(richContentFontClassName, styles.imageRatioSelectorRatioLabel),
    imageRatioSelector_ratioButton: classNames(
      'blog-text-after-background-color',
      styles.imageRatioSelectorRatioButton,
    ),
    imageRatioSelector_ratioButton_selected: classNames(
      'blog-button-background-color',
      styles.imageRatioSelectorRatioButtonSelected,
    ),
    imageRatioSelector_ratioIcon: classNames('blog-text-border-color', styles.imageRatioSelectorRatioIcon),
    imageRatioSelector_ratioIcon_selected: classNames(
      'blog-button-primary-text-border-color',
      styles.imageRatioSelectorRatioIconSelected,
    ),

    settingsPanel_footer: classNames(classes.opaqueBackgroundColor, classes.dividerBorderColor),
    section: classNames('blog-text-color', richContentFontClassName),

    imageSettings: classNames('blog-text-color', styles.imageSettings),
    imageSettingsTitle: classNames('blog-post-title-font', styles.imageSettingsTitle),
    imageCaption: imageCaptionClass,

    topBar: classNames(richContentFontClassName),
    topBarLink: classNames('blog-text-color', 'blog-button-hover-color'),
    filesButton: classNames('blog-text-color', 'blog-button-hover-color'),
    filesItem: classNames('blog-text-color', classes.dividerBorderColor),
    itemContainer: styles.itemContainer,
    itemContainerSelected: classNames('blog-button-color', styles.itemContainerSelected),
    gallerySettings_title: classNames(
      'blog-post-title-font',
      'blog-text-color',
      styles.gallerySettingsTitle,
      classes.opaqueBackgroundColor,
      classes.dividerBorderColor,
    ),
    gallerySettings_divider: classes.dividerBorderColor,

    cameraIcon: 'blog-icon-fill',
    video_modal_container_big: classNames(classes.opaqueBackgroundColor),
    video_modal_container_small: classNames(classes.opaqueBackgroundColor),
    video_modal_add_a_Video: classNames('blog-post-title-font', 'blog-post-title-color', styles.addVideo),
    video_modal_header_text: classNames('blog-post-description-font', 'blog-text-color', styles.headerText),
    video_modal_add_button_inline: classNames('blog-button-background-color'),
    video_modal_add_button_inMiddle: classNames('blog-button-background-color'),
    video_modal_or_upload_video_from: classNames(
      'blog-post-description-font',
      'blog-text-color',
      styles.orUploadVideoFrom,
    ),
    video_modal_upload_video: classNames(
      'blog-post-description-font',
      'blog-button-color',
      'blog-button-hover-color',
      styles.orUploadVideoFrom,
    ),
    textInput_input: classNames(
      'blog-text-color',
      richContentFontClassName,
      classes.dividerBorderColor,
      classes.focusedInputBorderColor,
      styles.textInputInput,
    ),

    galleryImageSettings: classNames(styles.galleryImageSettings, richContentFontClassName),
    galleryImageSettings_title: classNames(
      styles.galleryImageSettingsTitle,
      classes.opaqueBackgroundColor,
      'blog-text-color',
      'blog-post-title-font',
    ),
    galleryImageSettings_backIcon: 'blog-text-color',
    galleryImageSettings_delete: classNames(styles.galleryImageSettingsDelete, 'blog-text-color'),
    galleryImageSettings_replace: 'blog-text-color',

    htmlEditPanel_primaryButton: 'blog-button-color',
    htmlEditPanel_secondaryButton: 'blog-text-color',

    textColorModal: classNames(classes.opaqueBackgroundColor, classes.dividerBorderColor),
    textColorModal_mobile: classes.opaqueBackgroundColor,

    colorPicker_separator: css`
      background: ${dividerColor};
    `,
    colorPicker_add_color_label: 'blog-button-color',
    colorPicker_button: styles.colorPickerButton,
    colorPicker_button_selected: classNames(
      css`
        &:after {
          border-color: ${textColor} !important;
        }
      `,
      styles.colorPickerButtonSelected,
    ),
    colorPickerDialog_separator: classNames(
      classes.dividerBackgroundColor,
      css`
        margin-top: 1px;
      `,
    ),
    colorPickerDialog_buttons: 'blog-post-description-font',
    colorPickerDialog_button: classNames(styles.colorPickerDialogButton, 'blog-text-color'),
    colorPickerDialog_button_update: 'blog-button-color',

    customColorPicker_editable_input_container: classNames('blog-post-description-font', 'blog-text-color'),
    customColorPicker_input_container: styles.customColorPickerInputContainer,
    customColorPicker_input_label: styles.customColorPickerInputLabel,

    giphy_api_input_modal_container: 'blog-post-description-font',
    giphy_api_input_modal_search_textinput_group: classes.dividerBorderColor,
    giphy_api_input_modal_search: classNames('blog-text-color', styles.giphyModalSearchInput),
  };
}

export const createTheme = ({
  appSettings,
  readOnly,
  isCompact,
  tooltipsEnabled,
  isViewer,
  isSeo,
  isMobile,
  isPostPageEnabled,
  richContentFontClassName,
  useDescriptionFontInEditor,
  isBusinessManager,
  experiments,
  css,
}) => {
  let theme = getTheme({
    appSettings,
    readOnly,
    isCompact,
    tooltipsEnabled,
    isViewer,
    isSeo,
    isMobile,
    isPostPageEnabled,
    richContentFontClassName,
    useDescriptionFontInEditor,
    css,
  });

  if (isBusinessManager && experiments.isBusinessManagerFixedThemeEnabled) {
    theme = applyFixedBusinessManagerStylesToTheme({ theme, css });
  }

  return theme;
};
